import { useApp } from 'contexts/app';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { exportInstructorFormativeChatToCSV } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, currentAssignment, studentAccountUsers, currentAssignmentId, studentSubmissions } = useApp();
    const history = useHistory();
    const { submissionId } = useParams();
    const { t } = useTranslation();

    const [tab, setTab] = useState('feedback');
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [selectedInlineCommentId, setSelectedInlineCommentId] = useState(null);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);

    const studentSubmissionsGroupedByAccountUser = useMemo(() => {
        return studentAccountUsers?.map((accountUser) => {
            return {
                ...accountUser,
                submissions: studentSubmissions?.filter(
                    ({ owner_account_id }) => owner_account_id === accountUser.owner_account_id
                ),
            };
        });
    }, [studentAccountUsers, studentSubmissions]);
    const currentSubmission = useMemo(
        () => studentSubmissions?.find(({ id }) => id === submissionId),
        [studentSubmissions, submissionId]
    );
    const currentStudentSubmissions = useMemo(() => {
        const targetOwnerAccountId = currentSubmission.owner_account_id;
        return studentSubmissions?.filter(({ owner_account_id }) => owner_account_id === targetOwnerAccountId);
    }, [currentSubmission.owner_account_id, studentSubmissions]);
    const currentSubmissionIndex = currentStudentSubmissions
        ?.sort((a, b) => a.created_on - b.created_on)
        ?.findIndex(({ id }) => id === submissionId);

    const getHighlights = useCallback(() => {
        const format = (items) => {
            return (items ?? []).map((item) => {
                return {
                    id: selectedFeedbackId,
                    value: item,
                };
            });
        };

        if (selectedFeedbackId) {
            const feedback = currentSubmission?.ai?.feedback;
            const isRubric = feedback?.feedback_type === 'rubric';
            const suggestion = currentSubmission?.ai?.feedback?.improvement_suggestions?.find(
                ({ id }) => id === selectedFeedbackId
            );
            const locations = isRubric ? suggestion?.negative?.locations : suggestion?.locations;
            return format(locations);
        }

        const comments = currentSubmission?.ai?.inline_comments ?? [];
        const activeHighlights = comments
            .filter(({ active }) => active)
            .filter(({ id }) => id === selectedInlineCommentId)
            .map(({ highlight }) => highlight);

        return format(activeHighlights);
    }, [
        currentSubmission?.ai?.feedback,
        currentSubmission?.ai?.inline_comments,
        selectedFeedbackId,
        selectedInlineCommentId,
    ]);

    const onClickPrevAttempt = () => {
        const prevSubmissionId = currentStudentSubmissions[currentSubmissionIndex - 1].id;
        history.push(`/assignments/${currentAssignmentId}/submissions/${prevSubmissionId}`);
    };

    const onClickNextAttempt = () => {
        const nextSubmissionId = currentStudentSubmissions[currentSubmissionIndex + 1].id;
        history.push(`/assignments/${currentAssignmentId}/submissions/${nextSubmissionId}`);
    };

    const onChangeStudent = ({ key }) => {
        const targetSubmission = studentSubmissions
            .filter(({ owner_account_id }) => owner_account_id === key)
            .slice(-1)[0];
        history.push(`/assignments/${currentAssignmentId}/submissions/${targetSubmission.id}`);
    };

    const onClickExit = () => {
        const params = new URLSearchParams({ owner_account_id: currentSubmission.owner_account_id, type: 'formative' });
        history.push(`/assignments/${currentAssignmentId}/submissions?${params.toString()}`);
    };

    const onClickViewNext = () => {
        const viewableStudents = studentSubmissionsGroupedByAccountUser.filter(({ submissions }) =>
            Boolean(submissions.length)
        );
        const nextStudentIndex =
            viewableStudents.findIndex(
                ({ owner_account_id }) => owner_account_id === currentSubmission.owner_account_id
            ) + 1;
        if (viewableStudents[nextStudentIndex]) {
            onChangeStudent({ key: viewableStudents[nextStudentIndex].owner_account_id });
        }
    };

    const onDownloadChatCsv = async () => {
        if (!currentAssignment?.id || !course?.id || !currentSubmission?.owner_account_id) return;

        try {
            const currentStudent = studentAccountUsers?.find(
                ({ owner_account_id }) => owner_account_id === currentSubmission.owner_account_id
            );

            await exportInstructorFormativeChatToCSV({
                assignment: currentAssignment,
                course,
                ownerAccountId: currentSubmission.owner_account_id,
                studentAccountUser: currentStudent,
                t,
            });
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (currentSubmission) {
            setTab('feedback');
            setSelectedFeedbackId(currentSubmission.ai.feedback?.improvement_suggestions?.[0]?.id);
        }
    }, [currentSubmission]);

    useEffect(() => {
        if (selectedFeedbackId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedFeedbackId]);

    useEffect(() => {
        if (selectedInlineCommentId !== null) {
            setSelectedFeedbackId(null);
        }
    }, [selectedInlineCommentId]);

    return (
        <PageContext.Provider
            value={{
                tab,
                setTab,
                selectedFeedbackId,
                setSelectedFeedbackId,
                selectedInlineCommentId,
                setSelectedInlineCommentId,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                studentSubmissionsGroupedByAccountUser,
                currentSubmission,
                currentStudentSubmissions,
                currentSubmissionIndex,
                highlights: getHighlights(),
                onClickPrevAttempt,
                onClickNextAttempt,
                onChangeStudent,
                onClickExit,
                onClickViewNext,
                onDownloadChatCsv,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
